import { useMemo } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import PageHeader from "@/components/feature/PageHeader";
import { useSeo } from "@/hooks/useSeo";
import { siteInfo, principles, testimonials } from "@/mocks/site";

const positioning = [
  {
    icon: "ri-group-line",
    title: "面向公众",
    description: "提供一条不绕路的核实入口：有疑问时先查公告，找不到再提交线索。",
  },
  {
    icon: "ri-newspaper-line",
    title: "面向内容创作者",
    description: "保留公告原文、发布时间与归档编号，方便引用与逐句比对，减少二次转述带来的偏差。",
  },
  {
    icon: "ri-team-line",
    title: "面向合作方",
    description: "把对外口径集中到一处，合作沟通时可以直接引用同一版本，避免各自解释。",
  },
];

const milestones = [
  { year: "2021", title: "建立归档机制", text: "开始以固定格式留存每一次正式表态，形成可检索的公告编号体系。" },
  { year: "2023", title: "开放线索提交", text: "把散落在各个渠道的核实请求收敛到统一入口，并配发受理编号。" },
  { year: "2025", title: "完善分类体系", text: "将公告划分为官方声明、辟谣澄清、官方回应与公告通知四类，按性质归档。" },
  { year: "2026", title: "升级检索体验", text: "上线关键词搜索、标签定位与时间排序，历史公告全部完成编号补齐。" },
];

export default function AboutPage() {
  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于黑料官方",
        inLanguage: "zh-CN",
        description: "黑料官方的平台定位、发展理念、内容原则与联系方式说明。",
        mainEntity: {
          "@type": "Organization",
          name: siteInfo.name,
          email: siteInfo.email,
          description: siteInfo.intro,
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "/" },
          { "@type": "ListItem", position: 2, name: "关于我们", item: "/about" },
        ],
      },
    ],
    [],
  );

  useSeo({
    title: "关于我们 - 黑料官方平台定位与内容原则",
    description:
      "了解黑料官方的平台定位、发展理念、内容原则与联系方式。我们以原文优先、来源可溯的方式，集中归档官方声明、辟谣澄清与公告通知。",
    keywords: "关于黑料官方,平台定位,内容原则,官方声明归档,信息核实",
    path: "/about",
    jsonLd,
  });

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <PageHeader
          eyebrow="关于平台"
          title="关于黑料官方"
          description="我们做的事情很具体：把每一次正式表态按统一格式留下来，让任何人在需要的时候都能找到官方原文，而不是拼凑来的截图。"
          crumbs={[{ label: "首页", to: "/" }, { label: "关于我们" }]}
        />

        <section aria-labelledby="about-who" className="mx-auto max-w-content px-4 py-16 md:px-6 md:py-20">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <h2 id="about-who" className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                一个专注于「官方口径」的信息平台
              </h2>
              <div className="mt-5 space-y-4 text-[15px] leading-8 text-foreground-700">
                <p>
                  {siteInfo.intro}
                </p>
                <p>
                  我们不生产观点，也不参与争论。平台的工作是把官方声明、回应、辟谣与公告整理成可检索的档案：标清发布时间与归档编号，保留完整原文，必要时补充后续说明。
                </p>
                <p>
                  当某条传闻开始扩散，你最先需要的是「谁在什么时候说了什么」。这句话看起来简单，但往往散在动态、截图和转述里。我们把它收拢到一处，就是为了让核实这件事变得更快、更准。
                </p>
              </div>
              <div className="mt-8 grid grid-cols-2 gap-5 sm:grid-cols-4">
                {siteInfo.stats.map((stat) => (
                  <div key={stat.label}>
                    <div className="font-heading text-2xl font-black text-primary-600">{stat.value}</div>
                    <div className="mt-1 text-xs text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="h-72 w-full overflow-hidden rounded-lg border border-background-200 bg-background-200 lg:h-96">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20editorial%20still%20life%20of%20neatly%20stacked%20cream%20paper%20documents%20with%20red%20seal%20markers%20on%20a%20beige%20desk%2C%20soft%20natural%20window%20light%2C%20muted%20gold%20accents%2C%20minimal%20calm%20composition%2C%20no%20text%2C%20high%20detail&width=1000&height=1200&seq=hl-about-01&orientation=portrait"
                  alt="黑料官方归档整理中的公告文档与印章"
                  title="黑料官方公告归档整理"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        <section aria-labelledby="about-positioning" className="bg-background-100 py-16 md:py-20">
          <div className="mx-auto max-w-content px-4 md:px-6">
            <h2 id="about-positioning" className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
              平台定位
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
              对不同的人来说，这里承担的角色不太一样。
            </p>
            <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
              {positioning.map((item) => (
                <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-xl text-accent-800">
                    <i className={item.icon} />
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section aria-labelledby="about-principles" className="mx-auto max-w-content px-4 py-16 md:px-6 md:py-20">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <h2 id="about-principles" className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                内容原则
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                原则听起来抽象，落到操作上就是四件坚持了很久的小事。
              </p>
              <div className="mt-6 h-1 w-16 rounded-full bg-primary-500" />
            </div>
            <ul className="lg:col-span-8">
              {principles.map((item, index) => (
                <li
                  key={item.title}
                  className="flex items-start gap-5 border-b border-background-200 py-5 first:pt-0 last:border-b-0"
                >
                  <span className="font-heading text-2xl font-black text-primary-500/60">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.description}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section aria-labelledby="about-timeline" className="bg-background-100 py-16 md:py-20">
          <div className="mx-auto max-w-content px-4 md:px-6">
            <h2 id="about-timeline" className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
              发展理念与历程
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
              从「先留档」到「可检索」，我们一直在做同一件事的更好版本。
            </p>
            <ol className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
              {milestones.map((item, index) => (
                <li key={item.year} className="relative rounded-lg border border-background-200 bg-background-50 p-6">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-500 text-xs font-bold text-background-50">
                    {index + 1}
                  </span>
                  <div className="mt-4 font-heading text-2xl font-black text-foreground-950">{item.year}</div>
                  <h3 className="mt-2 font-heading text-base font-bold text-foreground-900">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section aria-labelledby="about-voice" className="mx-auto max-w-content px-4 py-16 md:px-6 md:py-20">
          <h2 id="about-voice" className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
            用户口碑
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {testimonials.map((item) => (
              <figure key={item.author} className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="flex h-8 w-8 items-center justify-center text-2xl text-accent-500">
                  <i className="ri-double-quotes-l" />
                </span>
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                  {item.quote}
                </blockquote>
                <figcaption className="mt-5 border-t border-background-200 pt-4">
                  <span className="block font-heading text-sm font-bold text-foreground-950">{item.author}</span>
                  <span className="mt-0.5 block text-xs text-foreground-500">{item.role}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section aria-labelledby="about-contact" className="mx-auto max-w-content px-4 pb-4 md:px-6">
          <div className="rounded-lg border border-background-200 bg-secondary-950 p-8 md:p-12">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-center">
              <div>
                <h2 id="about-contact" className="font-heading text-2xl font-black text-background-50 md:text-3xl">
                  与我们联系
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-background-200/80">
                  核实请求、冒名举报与合作沟通，都欢迎通过统一入口提交，我们会按顺序处理并保留记录。
                </p>
              </div>
              <div className="flex flex-col gap-4 sm:flex-row lg:justify-end">
                <div className="rounded-md border border-background-50/15 bg-background-50/5 px-5 py-4">
                  <span className="block text-xs text-background-200/70">澄清与核实邮箱</span>
                  <span className="mt-1 block break-all text-sm text-background-50">{siteInfo.email}</span>
                </div>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-4 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                >
                  <span className="flex h-4 w-4 items-center justify-center"><i className="ri-send-plane-line" /></span>
                  提交线索
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}